/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Mon Apr 08 2024
 **/

import { cloneDeep } from "lodash-es";

import { useFileManager } from "@/frameworks/core/hooks";
import { useFileExtensions } from "@/frameworks/core/utils";
import { asyncGenerateVideoThumbnail } from "@/frameworks/libraries/utilities/videoToThumbnail";
import { useProperties } from "@/frameworks/libraries/properties";

type HookParams = {
	myRoomID?: string;
	spaceID?: string;
	roomID: string;
	type: "Space" | "DM" | "CONFERENCE";
	myRoom: boolean;
};

type FileFormat = ChatServices.Format2.Elements.File | ChatServices.Format2.Elements.Image;

export default function useUploadChat(params: HookParams) {
	const { uploadFile, isExistsFilename, saveLink } = useFileManager();
	const { getFileExtension } = useFileExtensions();
	const properties = useProperties();

	const uploadLinkToMyRoom = (urls: string[]) => {
		return saveLink({
			id: params.myRoomID || "",
			referenceType: "INDIVIDUAL",
			referenceId: params.myRoomID || "",
			urls: urls
		});
	};

	const uploadLinkToSpace = (urls: string[]) => {
		return saveLink({
			id: params.roomID || "",
			referenceType: "SPACE",
			referenceId: params.spaceID || "",
			urls: urls
		});
	};

	const uploadLinkToChat = (urls: string[]) => {
		return saveLink({
			id: params.roomID || "",
			referenceType: "CHAT",
			referenceId: params.roomID || "",
			urls: urls
		});
	};

	const uploadFileToChat = (tusID: string, fileName: string, file: File) => {
		return new Promise<FileFormat & { fileBoxId: string }>((resolve, reject) => {
			const afterSuccessFileUploadAPI = (fileUploadResult: FileBox.Upload.UploadResponse) => {
				const type = getFileExtension(fileUploadResult.fileMimeType || "") === "image" ? "image" : "file";
				if (type === "file") {
					const fileFormat: ChatServices.Format2.Elements.File & { fileBoxId: string } = {
						type: "file",
						id: fileUploadResult.id,
						fileBoxId: fileUploadResult.id,
						title: fileName,
						url: fileUploadResult.filePath ?? "",
						mime: fileUploadResult.fileMimeType || "",
						size: fileUploadResult.fileSize ?? 0,
						displaySize: fileUploadResult.fileDisplaySize ?? "",
						docThumbnailPath: fileUploadResult.docThumbnailPath ?? "",
						videoThumbnailPath: fileUploadResult.videoThumbnailPath ?? ""
					};
					resolve(fileFormat);
				}

				if (type === "image") {
					const fileFormat: ChatServices.Format2.Elements.Image & { fileBoxId: string } = {
						type: "image",
						id: fileUploadResult.id,
						fileBoxId: fileUploadResult.id,
						title: fileName,
						url: `${properties.default.webDomain}${fileUploadResult.filePath}` ?? "",
						alt: fileUploadResult.title
					};
					resolve(fileFormat);
				}
			};

			const uploadToMyRoom = async (tusID: string) => {
				const isExistSameFileName = await isExistsFilename({
					id: params.spaceID!,
					name: encodeURI(fileName),
					referenceId: params.spaceID,
					referenceType: "INDIVIDUAL"
				});

				uploadFile({
					id: params.spaceID || "",
					referenceType: "INDIVIDUAL",
					referenceId: params.spaceID || "",
					uploadFileOption: isExistSameFileName ? "DUPLICATE" : "INCREASE",
					tusId: tusID
				})
					.then(afterSuccessFileUploadAPI)
					.catch(reject);
			};

			const uploadToChatRoom = async (tusID: string) => {
				try {
					const fileType = getFileExtension(file.type);
					let thumbnailFileBoxId: string | undefined;

					if (fileType === "video") {
						const { file: thumbnailFile } = await asyncGenerateVideoThumbnail({
							videoSource: file,
							seekTime: 1,
							thumbnailMaxHeight: 720
						});

						const thumbnailResponse = await uploadFile({
							id: params.roomID,
							referenceType: "VIDEO_THUMBNAIL",
							referenceId:
								params.type === "Space" || params.type === "CONFERENCE"
									? params.spaceID || ""
									: params.roomID,
							uploadFileOption: "DUPLICATE",
							file: thumbnailFile
						});
						thumbnailFileBoxId = thumbnailResponse.id;
					}

					await uploadFile({
						id: params.roomID,
						referenceType:
							params.type === "CONFERENCE" ? "CONFERENCE" : params.type === "Space" ? "SPACE" : "CHAT",
						referenceId:
							params.type === "Space" || params.type === "CONFERENCE"
								? params.spaceID || ""
								: params.roomID,
						uploadFileOption: "DUPLICATE",
						tusId: tusID,
						videoThumbnailFileBoxId: thumbnailFileBoxId
					}).then(afterSuccessFileUploadAPI);
				} catch (error) {
					console.error(error);
				}
			};

			if (params.myRoom) {
				uploadToMyRoom(tusID);
				return;
			}
			uploadToChatRoom(tusID);
		});
	};

	const saveLinkBeforeSubmit = (chatForm: ChatServices.Format2.Elements.AllRichtextElements[]) => {
		return new Promise<{ chatForm: ChatServices.Format2.Elements.AllRichtextElements[]; fileBoxId?: string }>(
			(resolve, reject) => {
				const form = cloneDeep(chatForm);
				let saveLinkPromise;

				for (const element of form) {
					if (element.type === "link") {
						const { myRoom } = params;
						const afterUploadSuccess = (response: FileBox.Link.SaveLinkResponse) => {
							element.info = {
								...response[0]
							};
							return element;
						};

						if (myRoom) {
							saveLinkPromise = uploadLinkToMyRoom([element.url]).then(afterUploadSuccess);
							continue;
						}

						if (params.type === "Space") {
							saveLinkPromise = uploadLinkToSpace([element.url]).then(afterUploadSuccess);
							continue;
						}

						saveLinkPromise = uploadLinkToChat([element.url]).then(afterUploadSuccess);
						continue;
					}
				}

				if (!saveLinkPromise) {
					resolve({ chatForm: form });
					return;
				}

				saveLinkPromise
					.then((res) => {
						resolve({ chatForm: form, fileBoxId: res.info.fileBoxId });
					})
					.catch((error) => {
						reject(error);
					});
			}
		);
	};

	return { uploadFileToChat, saveLinkBeforeSubmit };
}
